<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'
import CompanyDashboardCard from '@/components/dashboard/CompanyDashboardCard.vue'
import CompanyMetricCard from '@/components/dashboard/CompanyMetricCard.vue'
import CompanyQueueRow from '@/components/dashboard/CompanyQueueRow.vue'
import CompanyQuickAction from '@/components/dashboard/CompanyQuickAction.vue'

type CompanyDashboardPayload = {
  company?: {
    name?: string
    industry?: string
    location?: string
  }
  stats?: {
    active_jobs?: number
    total_employees?: number
    pending_applications?: number
    open_dispatches?: number
    timesheets_pending?: number
    field_tickets_pending?: number
    field_ticket_value?: number
    unpaid_invoices?: number
    ar_total?: number
    payroll_due_w2?: number
    payroll_due_1099?: number
    safety_incidents?: number
    high_tank_alerts?: number
    expenses_week?: number
  }
  jobs?: Array<Record<string, any>>
  crew_on_site?: Array<Record<string, any>>
  dispatches?: Array<Record<string, any>>
  field_tickets?: Array<Record<string, any>>
  tank_alerts?: Array<Record<string, any>>
  invoices?: Array<Record<string, any>>
  payroll?: Array<Record<string, any>>
  applications?: Array<Record<string, any>>
  safety?: Array<Record<string, any>>
  activity?: Array<Record<string, any>>
}

const router = useRouter()
const loading = ref(true)
const refreshing = ref(false)
const error = ref('')
const updatedAt = ref(new Date())

const dashboard = ref<CompanyDashboardPayload>({
  company: {
    name: 'Demo Oil Services Inc.',
    industry: 'Oilfield Services',
    location: 'North Dakota',
  },
  stats: {
    active_jobs: 0,
    total_employees: 0,
    pending_applications: 0,
    open_dispatches: 0,
    timesheets_pending: 0,
    field_tickets_pending: 0,
    field_ticket_value: 0,
    unpaid_invoices: 0,
    ar_total: 0,
    payroll_due_w2: 0,
    payroll_due_1099: 0,
    safety_incidents: 0,
    high_tank_alerts: 0,
    expenses_week: 0,
  },
  jobs: [],
  crew_on_site: [],
  dispatches: [],
  field_tickets: [],
  tank_alerts: [],
  invoices: [],
  payroll: [],
  applications: [],
  safety: [],
  activity: [],
})

const stats = computed(() => dashboard.value.stats || {})
const jobs = computed(() => dashboard.value.jobs || [])
const crew = computed(() => dashboard.value.crew_on_site || [])
const dispatches = computed(() => dashboard.value.dispatches || [])
const fieldTickets = computed(() => dashboard.value.field_tickets || [])
const tankAlerts = computed(() => dashboard.value.tank_alerts || [])
const invoices = computed(() => dashboard.value.invoices || [])
const payroll = computed(() => dashboard.value.payroll || [])
const applications = computed(() => dashboard.value.applications || [])
const safety = computed(() => dashboard.value.safety || [])
const activity = computed(() => dashboard.value.activity || [])

const money = (value: unknown) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(value || 0))

const today = computed(() =>
  updatedAt.value.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
)

function go(path: string) {
  router.push(path)
}

async function loadDashboard(showSpinner = true) {
  if (showSpinner) loading.value = true
  refreshing.value = !showSpinner
  error.value = ''

  try {
    const token = localStorage.getItem('auth_token')
    const response = await fetch('/api/company/dashboard-stats', {
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })

    if (!response.ok) throw new Error('Unable to load company dashboard')

    const payload = await response.json()

    dashboard.value = {
      ...dashboard.value,
      ...payload,
      company: {
        ...dashboard.value.company,
        ...(payload.company || {}),
      },
      stats: {
        ...dashboard.value.stats,
        ...(payload.stats || payload.dashboard_stats || {}),
        active_jobs: (payload.stats || {}).active_jobs ?? (payload.stats || {}).activeJobs ?? 0,
        total_employees: (payload.stats || {}).total_employees ?? (payload.stats || {}).totalEmployees ?? 0,
        pending_applications: (payload.stats || {}).pending_applications ?? (payload.stats || {}).pendingApplications ?? 0,
      },
      activity: payload.activity || payload.recentActivity || dashboard.value.activity,
    }
  } catch (e: any) {
    error.value = e?.message || 'Company dashboard data could not be loaded.'
  } finally {
    loading.value = false
    refreshing.value = false
    updatedAt.value = new Date()
  }
}

onMounted(() => loadDashboard())
</script>

<template>
  <CompanyAppLayout page-title="Company Dashboard" page-subtitle="Live operations, tickets, payroll, and hiring">
    <div class="mx-auto w-full max-w-[1700px]">
        <!-- Context strip -->
        <section class="mb-5 flex flex-col gap-4 rounded-2xl border border-slate-700/50 bg-slate-800/45 p-4 shadow-xl shadow-black/10 backdrop-blur-xl xl:flex-row xl:items-center xl:justify-between">
          <div class="flex min-w-0 items-center gap-3">
            <div class="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-400 font-black text-slate-950 shadow-lg shadow-emerald-500/15">
              {{ (dashboard.company?.name || 'OB').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() }}
            </div>

            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <h1 class="truncate text-2xl font-extrabold tracking-tight text-white">
                  Company Operations Dashboard
                </h1>
                <span class="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-emerald-300">
                  Live Operations
                </span>
              </div>

              <p class="mt-1 truncate text-sm text-slate-400">
                {{ dashboard.company?.name }} · {{ dashboard.company?.industry }} · {{ dashboard.company?.location }}
              </p>
            </div>
          </div>

          <div class="flex flex-wrap items-center gap-2 text-sm">
            <div class="rounded-xl border border-slate-700/50 bg-slate-900/45 px-3 py-2">
              <span class="text-slate-500">Today</span>
              <strong class="ml-2 text-white">{{ today }}</strong>
            </div>

            <div class="rounded-xl border border-slate-700/50 bg-slate-900/45 px-3 py-2">
              <span class="text-slate-500">Updated</span>
              <strong class="ml-2 text-white">{{ updatedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) }}</strong>
            </div>

            <button
              class="rounded-xl border border-slate-600/50 bg-slate-700/50 px-4 py-2 font-semibold text-slate-200 transition hover:border-emerald-500/30 hover:text-emerald-300"
              :disabled="refreshing"
              @click="loadDashboard(false)"
            >
              {{ refreshing ? 'Refreshing…' : '↻ Refresh' }}
            </button>
          </div>
        </section>

        <div v-if="error" class="mb-5 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {{ error }}
        </div>

        <div v-if="loading" class="space-y-5">
          <div class="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
            <div v-for="n in 6" :key="n" class="h-32 animate-pulse rounded-2xl border border-slate-700/50 bg-slate-800/40"></div>
          </div>
          <div class="grid gap-5 xl:grid-cols-12">
            <div class="h-96 animate-pulse rounded-2xl border border-slate-700/50 bg-slate-800/40 xl:col-span-7"></div>
            <div class="h-96 animate-pulse rounded-2xl border border-slate-700/50 bg-slate-800/40 xl:col-span-5"></div>
          </div>
        </div>

        <template v-else>
          <!-- Actionable KPIs -->
          <section class="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
            <CompanyMetricCard
              title="Active Jobs"
              :value="stats.active_jobs ?? 0"
              subtitle="Currently in progress"
              icon="job"
              tone="emerald"
              @click="go('/company/jobs')"
            />
            <CompanyMetricCard
              title="Crew On Site"
              :value="crew.length"
              subtitle="Workers checked in"
              icon="crew"
              tone="cyan"
              @click="go('/company/onsite')"
            />
            <CompanyMetricCard
              title="Timesheets Pending"
              :value="stats.timesheets_pending ?? 0"
              subtitle="Need approval"
              icon="clock"
              tone="amber"
              @click="go('/company/timesheets')"
            />
            <CompanyMetricCard
              title="Field Tickets"
              :value="stats.field_tickets_pending ?? 0"
              subtitle="Ready for review / invoice"
              icon="ticket"
              tone="purple"
              @click="go('/company/field-tickets')"
            />
            <CompanyMetricCard
              title="Tank Alerts"
              :value="stats.high_tank_alerts ?? 0"
              subtitle="High level / exception"
              icon="tank"
              tone="red"
              @click="go('/company/tank-sheets')"
            />
            <CompanyMetricCard
              title="Applications"
              :value="stats.pending_applications ?? 0"
              subtitle="Need decision"
              icon="application"
              tone="blue"
              @click="go('/company/applications')"
            />
          </section>

          <!-- Current operations -->
          <section class="mb-5 grid gap-5 xl:grid-cols-12">
            <CompanyDashboardCard
              class="xl:col-span-5"
              title="Active Operations"
              subtitle="Jobs, wells and location status"
              action-label="Manage Jobs"
              @action="go('/company/jobs')"
            >
              <div class="space-y-2">
                <CompanyQueueRow
                  v-for="(job, i) in jobs.slice(0, 6)"
                  :key="job.id || i"
                  :title="job.well_name || job.title || `Job ${i + 1}`"
                  :subtitle="[job.customer, job.location, job.service_type].filter(Boolean).join(' · ')"
                  :meta="job.status || ''"
                  :tone="job.status === 'active' || job.status === 'on_location' ? 'emerald' : job.status === 'warning' ? 'amber' : 'cyan'"
                />

                <div v-if="!jobs.length" class="grid min-h-64 place-items-center rounded-xl border border-dashed border-slate-700/60 bg-slate-900/25 p-6 text-center">
                  <div>
                    <div class="text-2xl">⛽</div>
                    <p class="mt-2 font-semibold text-white">No active jobs</p>
                    <p class="mt-1 text-sm text-slate-500">Active wells and locations will appear here.</p>
                    <button class="mt-4 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-bold text-slate-950" @click="go('/company/post-job')">Post Job</button>
                  </div>
                </div>
              </div>
            </CompanyDashboardCard>

            <CompanyDashboardCard
              class="xl:col-span-4"
              title="Quick Actions"
              subtitle="Most common company tasks"
            >
              <div class="grid grid-cols-2 gap-3">
                <CompanyQuickAction label="Dispatch Crew" icon="dispatch" tone="emerald" @click="go('/company/dispatch')" />
                <CompanyQuickAction label="Approve Timesheets" icon="time" tone="amber" @click="go('/company/timesheets')" />
                <CompanyQuickAction label="Field Tickets" icon="ticket" tone="purple" @click="go('/company/field-tickets')" />
                <CompanyQuickAction label="Tank Sheets" icon="tank" tone="cyan" @click="go('/company/tank-sheets')" />
                <CompanyQuickAction label="Manage Crew" icon="crew" tone="blue" @click="go('/company/crew')" />
                <CompanyQuickAction label="Payroll" icon="money" tone="emerald" @click="go('/company/payroll')" />
                <CompanyQuickAction label="Safety" icon="shield" tone="amber" @click="go('/company/safety')" />
                <CompanyQuickAction label="AI Assistant" icon="ai" tone="purple" @click="go('/company/ai-messaging')" />
              </div>
            </CompanyDashboardCard>

            <CompanyDashboardCard
              class="xl:col-span-3"
              title="Operational Alerts"
              subtitle="Exceptions requiring action"
            >
              <div class="space-y-2">
                <CompanyQueueRow
                  v-for="(item, i) in tankAlerts.slice(0, 3)"
                  :key="`tank-${item.id || i}`"
                  :title="item.title || item.tank_name || 'Tank Alert'"
                  :subtitle="item.message || item.location || ''"
                  :meta="item.time || item.created_at || ''"
                  tone="red"
                />

                <CompanyQueueRow
                  v-for="(item, i) in safety.slice(0, 2)"
                  :key="`safety-${item.id || i}`"
                  :title="item.title || item.type || 'Safety Alert'"
                  :subtitle="item.location || item.description || ''"
                  :meta="item.time || item.created_at || ''"
                  :tone="item.severity === 'high' ? 'red' : 'amber'"
                />

                <div v-if="!tankAlerts.length && !safety.length" class="grid min-h-52 place-items-center rounded-xl bg-slate-900/25 p-5 text-center">
                  <div>
                    <div class="text-2xl">✓</div>
                    <p class="mt-2 font-semibold text-white">No active alerts</p>
                    <p class="mt-1 text-xs text-slate-500">Tank and safety exceptions will appear here.</p>
                  </div>
                </div>
              </div>
            </CompanyDashboardCard>
          </section>

          <!-- Crew / Dispatch / Tickets -->
          <section class="mb-5 grid gap-5 xl:grid-cols-12">
            <CompanyDashboardCard
              class="xl:col-span-4"
              title="Crew On Site"
              subtitle="Live location headcount"
              action-label="On Site Hub"
              @action="go('/company/onsite')"
            >
              <div class="space-y-2">
                <CompanyQueueRow
                  v-for="(person, i) in crew.slice(0, 6)"
                  :key="person.id || i"
                  :title="person.name || `Worker ${i + 1}`"
                  :subtitle="[person.role, person.location].filter(Boolean).join(' · ')"
                  :meta="person.shift || person.status || ''"
                  tone="emerald"
                />
                <div v-if="!crew.length" class="rounded-xl border border-dashed border-slate-700/60 p-5 text-center text-sm text-slate-500">
                  No crew currently checked in.
                </div>
              </div>
            </CompanyDashboardCard>

            <CompanyDashboardCard
              class="xl:col-span-4"
              title="Open Dispatch"
              subtitle="Calls and assignments in motion"
              action-label="Dispatch"
              @action="go('/company/dispatch')"
            >
              <div class="space-y-2">
                <CompanyQueueRow
                  v-for="(item, i) in dispatches.slice(0, 6)"
                  :key="item.id || i"
                  :title="item.title || item.service_type || 'Dispatch'"
                  :subtitle="[item.well_name, item.location, item.assignee].filter(Boolean).join(' · ')"
                  :meta="item.status || item.start_time || ''"
                  :tone="item.status === 'en_route' ? 'cyan' : item.status === 'late' ? 'red' : 'emerald'"
                />
                <div v-if="!dispatches.length" class="rounded-xl border border-dashed border-slate-700/60 p-5 text-center text-sm text-slate-500">
                  No open dispatches.
                </div>
              </div>
            </CompanyDashboardCard>

            <CompanyDashboardCard
              class="xl:col-span-4"
              title="Field Tickets to Invoice"
              subtitle="Completed work moving to billing"
              action-label="Field Tickets"
              @action="go('/company/field-tickets')"
            >
              <div class="mb-4 grid grid-cols-2 gap-3">
                <div class="rounded-xl border border-purple-500/15 bg-purple-500/[.05] p-4">
                  <span class="text-xs text-slate-500">Pending Tickets</span>
                  <strong class="mt-1 block text-xl text-purple-300">{{ stats.field_tickets_pending ?? 0 }}</strong>
                </div>
                <div class="rounded-xl border border-emerald-500/15 bg-emerald-500/[.05] p-4">
                  <span class="text-xs text-slate-500">Ticket Value</span>
                  <strong class="mt-1 block text-xl text-emerald-300">{{ money(stats.field_ticket_value) }}</strong>
                </div>
              </div>

              <div class="space-y-2">
                <CompanyQueueRow
                  v-for="(item, i) in fieldTickets.slice(0, 4)"
                  :key="item.id || i"
                  :title="item.ticket_number || item.number || `Ticket ${i + 1}`"
                  :subtitle="[item.customer, item.well_name].filter(Boolean).join(' · ')"
                  :meta="item.status || ''"
                  :tone="item.status === 'approved' ? 'emerald' : item.status === 'rejected' ? 'red' : 'purple'"
                />
                <div v-if="!fieldTickets.length" class="rounded-xl border border-dashed border-slate-700/60 p-4 text-center text-sm text-slate-500">
                  No tickets waiting for billing.
                </div>
              </div>
            </CompanyDashboardCard>
          </section>

          <!-- Money / payroll / hiring -->
          <section class="mb-5 grid gap-5 xl:grid-cols-12">
            <CompanyDashboardCard
              class="xl:col-span-5"
              title="Receivables & Cash Flow"
              subtitle="Invoices, AR and weekly spend"
              action-label="Payments & Invoices"
              @action="go('/company/payments')"
            >
              <div class="grid gap-3 sm:grid-cols-3">
                <div class="rounded-xl border border-slate-700/50 bg-slate-900/30 p-4">
                  <span class="text-xs text-slate-500">Accounts Receivable</span>
                  <strong class="mt-1 block text-xl text-white">{{ money(stats.ar_total) }}</strong>
                </div>
                <div class="rounded-xl border border-red-500/15 bg-red-500/[.05] p-4">
                  <span class="text-xs text-slate-500">Unpaid Invoices</span>
                  <strong class="mt-1 block text-xl text-red-300">{{ stats.unpaid_invoices ?? 0 }}</strong>
                </div>
                <div class="rounded-xl border border-amber-500/15 bg-amber-500/[.05] p-4">
                  <span class="text-xs text-slate-500">Expenses This Week</span>
                  <strong class="mt-1 block text-xl text-amber-300">{{ money(stats.expenses_week) }}</strong>
                </div>
              </div>

              <div class="mt-4 space-y-2">
                <CompanyQueueRow
                  v-for="(item, i) in invoices.slice(0, 4)"
                  :key="item.id || i"
                  :title="item.number || item.reference || `Invoice ${i + 1}`"
                  :subtitle="item.customer || item.description || ''"
                  :meta="money(item.amount)"
                  :tone="item.status === 'paid' ? 'emerald' : item.status === 'overdue' ? 'red' : 'amber'"
                />
                <div v-if="!invoices.length" class="rounded-xl border border-dashed border-slate-700/60 p-4 text-center text-sm text-slate-500">
                  Invoice activity will appear here.
                </div>
              </div>
            </CompanyDashboardCard>

            <CompanyDashboardCard
              class="xl:col-span-4"
              title="Payroll Due"
              subtitle="W-2 and 1099 obligations"
            >
              <div class="grid grid-cols-2 gap-3">
                <button class="rounded-xl border border-cyan-500/15 bg-cyan-500/[.05] p-4 text-left hover:border-cyan-500/30" @click="go('/company/payroll')">
                  <span class="text-xs text-slate-500">W-2 Payroll Due</span>
                  <strong class="mt-1 block text-xl text-cyan-300">{{ money(stats.payroll_due_w2) }}</strong>
                  <span class="mt-2 block text-[10px] text-slate-500">Open W-2 Payroll →</span>
                </button>
                <button class="rounded-xl border border-emerald-500/15 bg-emerald-500/[.05] p-4 text-left hover:border-emerald-500/30" @click="go('/company/contractor-payroll')">
                  <span class="text-xs text-slate-500">1099 Payroll Due</span>
                  <strong class="mt-1 block text-xl text-emerald-300">{{ money(stats.payroll_due_1099) }}</strong>
                  <span class="mt-2 block text-[10px] text-slate-500">Open 1099 Payroll →</span>
                </button>
              </div>

              <div class="mt-4 space-y-2">
                <CompanyQueueRow
                  v-for="(item, i) in payroll.slice(0, 4)"
                  :key="item.id || i"
                  :title="item.title || item.payee || `Payroll ${i + 1}`"
                  :subtitle="item.type || item.period || ''"
                  :meta="money(item.amount)"
                  :tone="item.type === 'W-2' ? 'cyan' : 'emerald'"
                />
              </div>
            </CompanyDashboardCard>

            <CompanyDashboardCard
              class="xl:col-span-3"
              title="Applications"
              subtitle="Candidates needing a decision"
              action-label="Applications"
              @action="go('/company/applications')"
            >
              <div class="space-y-2">
                <CompanyQueueRow
                  v-for="(item, i) in applications.slice(0, 5)"
                  :key="item.id || i"
                  :title="item.name || item.applicant_name || `Applicant ${i + 1}`"
                  :subtitle="item.position || item.role || ''"
                  :meta="item.status || 'Pending'"
                  tone="blue"
                />
                <div v-if="!applications.length" class="rounded-xl border border-dashed border-slate-700/60 p-5 text-center text-sm text-slate-500">
                  No pending applications.
                </div>
              </div>
            </CompanyDashboardCard>
          </section>

          <!-- Activity -->
          <section class="grid gap-5 xl:grid-cols-12">
            <CompanyDashboardCard
              class="xl:col-span-8"
              title="Recent Company Activity"
              subtitle="Operations, billing, payroll and safety"
            >
              <div class="grid gap-2 md:grid-cols-2">
                <CompanyQueueRow
                  v-for="(item, i) in activity.slice(0, 8)"
                  :key="item.id || i"
                  :title="item.title || item.description || 'Activity'"
                  :subtitle="item.description && item.title ? item.description : item.location || ''"
                  :meta="item.time || item.created_at || ''"
                  :tone="item.status === 'success' ? 'emerald' : item.status === 'warning' ? 'amber' : item.status === 'danger' ? 'red' : 'cyan'"
                />

                <div v-if="!activity.length" class="col-span-full grid min-h-48 place-items-center rounded-xl border border-dashed border-slate-700/60 p-6 text-center">
                  <div>
                    <div class="text-2xl">▣</div>
                    <p class="mt-2 font-semibold text-white">No recent activity</p>
                    <p class="mt-1 text-sm text-slate-500">Company activity will appear here as work happens.</p>
                  </div>
                </div>
              </div>
            </CompanyDashboardCard>

            <CompanyDashboardCard
              class="xl:col-span-4"
              title="Company Snapshot"
              subtitle="Headcount and safety"
              action-label="W-2 Dashboard"
              @action="go('/company/w2-dashboard')"
            >
              <div class="grid grid-cols-2 gap-3">
                <div class="rounded-xl border border-slate-700/50 bg-slate-900/30 p-4">
                  <span class="text-xs text-slate-500">Total Employees</span>
                  <strong class="mt-1 block text-xl text-white">{{ stats.total_employees ?? 0 }}</strong>
                </div>
                <div class="rounded-xl border border-red-500/15 bg-red-500/[.05] p-4">
                  <span class="text-xs text-slate-500">Safety Incidents</span>
                  <strong class="mt-1 block text-xl text-red-300">{{ stats.safety_incidents ?? 0 }}</strong>
                </div>
              </div>

              <div class="mt-4 grid grid-cols-2 gap-3">
                <button class="rounded-xl border border-slate-700/50 bg-slate-700/25 px-3 py-3 text-xs font-semibold hover:border-emerald-500/30" @click="go('/company/crew')">Manage Crew</button>
                <button class="rounded-xl border border-slate-700/50 bg-slate-700/25 px-3 py-3 text-xs font-semibold hover:border-emerald-500/30" @click="go('/company/safety')">Safety</button>
              </div>
            </CompanyDashboardCard>
          </section>
        </template>
    </div>
  </CompanyAppLayout>
</template>
